// adminEscalations.mjs — 管理员「agent 反馈」面的纯逻辑（第一百一十批 B ④）。
//
// ── 这一页存在的理由（需求方点名）─────────────────────────────────────────
// 「给管理员提供查看这些问题反馈的界面」。在它之前，agent 遇到做不了的事只有两种
// 归宿，两种都看不见：① 编一份自己都不信的结论交上去（落在成功路径上，没有人知道
// 它其实卡住了）；② 崩掉三次，然后在 `job.failedRows` 里躺着——那份表只有管理员看得
// 见、**且不按工单索引**，于是「MNT-000012 的预读怎么了」这个问题在页面上无解。
//
// ── 这一页问的问题与审批面**不是同一个** ────────────────────────────────────
// 审批人问的是「这张单怎么办」，所以他只需要**当前轮、未处置**的那一条，紧挨着
// 重跑按钮。管理员问的是「这套系统交派任务的能力出了什么系统性问题」，所以他要的是
// **横切**：按 reason 计数、跨工单、跨轮次、连已处置的也要能翻出来。
// 两个受众、两套文案（`admin.escalations.reason.*` 与 `agentReview.blocked.reason.*`
// 刻意不共用），于是也就是两只归一化——共用一只会逼出一堆 `forAdmin` 参数。
//
// ── byReason 为什么必须是**全集**计数 ───────────────────────────────────────
// 「逃生口有没有变成 agent 的默认出路」这个问题只有计数答得了，而分页后的计数答不了
// 它。所以服务端给的 `byReason` 不受 limit/offset 影响，这里也**绝不**从当前页的行
// 里重算——那会得出一个随翻页而变的比例，比没有比例更坏。
//
// 纯函数、无 DOM、无 React（SB-042：可判定逻辑入 .mjs 配单测）。
import { pageInfo } from './adminLogs.mjs';

// ── SB-159（第三百八十二批）：翻页 ─────────────────────────────────────────────
// 后端自第一百一十批就给 limit/offset/total，这一页却从不发 offset、也不渲染翻页器——在缺省的
// 「未处置」视图下，第 51 条及更早的未处置报告**静静消失**（SB-159 §2.2 点名「全仓最便宜的一处」）。
// 一页 50 条；翻页器读服务端的 total。**total 缺席 ⇒ unknown**，绝不当成「只有这一页」。
export const ESC_PAGE_LIMIT = 50;

// 复审（N4）：范围与下一页的 offset 按服务端**回显**的 limit／offset 算（缺席才退回调用方请求的那两个数）。
// 服务端会钳页长（routes_admin.cpp：1..200）——它若只给了 20 条而这里仍按请求的 50 往后跳，第 21–50 条
// 就被**静静跳过**：范围说「第 1–20 条」、下一页从 51 起，中间那 30 条哪一页都不在。
export function escalationPager(data, offset = 0, limit = ESC_PAGE_LIMIT) {
  const total = data?.total;
  if (typeof total !== 'number' || !Number.isFinite(total)) return { unknown: true };
  const shown = Array.isArray(data?.escalations) ? data.escalations.length : 0;
  const echoedLimit = Number(data?.limit);
  const echoedOffset = Number(data?.offset);
  const l = Number.isFinite(echoedLimit) && echoedLimit > 0 ? echoedLimit : limit;
  const o = Number.isFinite(echoedOffset) && echoedOffset >= 0 ? echoedOffset : Math.max(0, Number(offset) || 0);
  // 处置之后这一页空了（最后一条被 ack 掉、未处置视图里它就不在了）⇒ 退一页，而不是停在一张空表上。
  if (shown === 0 && o > 0) return { unknown: false, stepBack: true, prevOffset: Math.max(0, o - l) };
  const info = pageInfo(total, l, o);
  // showBar：total 为 0 时不出翻页器——空态那一句已经说了「没有」，旁边再挂一句「第 0–0 条，共 0 条」
  // 和两颗灰按钮只是噪音（复审 N7）。
  return { ...info, unknown: false, stepBack: false, from: shown ? o + 1 : 0, to: o + shown,
           showBar: info.total > 0 };
}

// 服务端 `agent_escalation.reason` 的枚举值域（副本；DP-2 的既有姿态）。
// 值域外的行**不丢**：管理面是最后一道能看见系统实情的地方，丢一行等于把一个
// 我们还不认识的新故障类别从统计里抹掉。处置是不出标签、reason 留空串。
export const ESCALATION_REASONS = Object.freeze([
  'insufficient_context', 'out_of_scope', 'contradictory_instructions',
  'tooling_blocked', 'unsafe_request',
]);

// 三档过滤。默认 **unacked**：这一页是待办面不是档案馆，默认列全量会让「还有几条
// 没人管」这个唯一要紧的数字淹在历史里。
export const ESCALATION_FILTERS = Object.freeze(['unacked', 'acked', 'all']);
export const DEFAULT_FILTER = 'unacked';

// 处置说明的上限（契约 E6：≤500 字符，且 trim 后非空）。
export const ACK_NOTE_MAX = 500;

// filter → 查询参数。`acked` 在契约里是三值字符串（false/true/all）而不是布尔，
// 所以这里产出的也是字符串——client.mjs 的 qs() 会跳过 undefined/''，
// 而 'false' 必须真的被发出去（不发＝服务端默认，恰好同值，但那是巧合不是契约）。
export function escalationQuery(filter, opts = {}) {
  const f = ESCALATION_FILTERS.includes(filter) ? filter : DEFAULT_FILTER;
  const q = { acked: f === 'unacked' ? 'false' : f === 'acked' ? 'true' : 'all' };
  if (opts.op) q.op = String(opts.op);
  if (opts.reason) q.reason = String(opts.reason);
  if (Number(opts.limit) > 0) q.limit = Math.min(200, Math.max(1, Number(opts.limit)));
  if (Number(opts.offset) > 0) q.offset = Number(opts.offset);
  return q;
}

// 一行的归一化。**ackAtMs 用 null 而不是 0**：0 是一个合法的 epoch，而这一列的
// 语义是"有没有被处置过"——把两者揉进同一个数字，第一条在 1970 年被处置的行
// （不会有，但判据不该建立在"不会有"上）就会读成未处置。
export function normalizeEscalation(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = Number(raw.id) || 0;
  if (!id) return null;               // 没有 id 就没法 ack，那一行是不可操作的
  const ackAtMs = Number(raw.ackAtMs) > 0 ? Number(raw.ackAtMs) : null;
  return {
    id,
    op: str(raw.op),
    entityKind: str(raw.entityKind),
    entityNo: str(raw.entityNo),
    entityId: Number(raw.entityId) || 0,
    projectGroup: str(raw.projectGroup),
    round: Number(raw.round) || 0,
    reason: ESCALATION_REASONS.includes(raw.reason) ? raw.reason : '',
    // ⚠ **纯文本**渲染，绝不 markdown：它可能复述 agent 读到的仓内容（第三方材料）。
    // 这只函数不做任何转义——转义是渲染层的事，而 React 的文本节点本就转义；
    // 这里写下来是为了让下一个人不要在这里"顺手 markdown 一下"。
    detail: str(raw.detail),
    wouldNeed: strArray(raw.wouldNeed),
    directives: strArray(raw.directives),
    toolTrace: (Array.isArray(raw.toolTrace) ? raw.toolTrace : [])
      .filter((c) => c && typeof c === 'object')
      .map((c) => ({ tool: str(c.tool), args: str(c.args), note: str(c.note),
                     ok: c.ok === true, bytes: Number(c.bytes) || 0 })),
    model: str(raw.model),
    jobId: Number(raw.jobId) || 0,
    createdAtMs: Number(raw.createdAtMs) || 0,
    ackAtMs,
    ackBy: raw.ackBy ?? null,
    ackNote: str(raw.ackNote),
  };
}

export function normalizeEscalations(raw) {
  return (Array.isArray(raw) ? raw : []).map(normalizeEscalation).filter(Boolean);
}

// 这一行处置过了吗。单向：服务端没有 unack，页面也不该有任何写法能把它读回未处置。
export const isAcked = (row) => row?.ackAtMs != null;

// reason → i18n 键。值域外返回 null ⇒ 不出标签（而不是印一个 `admin.escalations.
// reason.` 的裸键名，MUI 的 uppercase Chip 会把它变成一句给用户的乱码）。
export const reasonLabelKey = (reason) =>
  (ESCALATION_REASONS.includes(reason) ? `admin.escalations.reason.${reason}` : null);

// byReason 全集计数 → 有序列表（多的在前，同数按值域顺序稳定）。
// **不从当前页的行重算**（见头注）：这只函数的输入只能是服务端给的那份。
export function byReasonList(byReason) {
  const src = byReason && typeof byReason === 'object' ? byReason : {};
  return Object.keys(src)
    .filter((k) => Number(src[k]) > 0)
    .map((k) => ({ reason: k, n: Number(src[k]) }))
    .sort((a, b) => b.n - a.n ||
      ESCALATION_REASONS.indexOf(a.reason) - ESCALATION_REASONS.indexOf(b.reason));
}

// `{list}` 占位符的文本。标签函数由调用侧注入（i18n 的 t 在 React 里，这里不碰）
// ——纯函数因此仍可穷举测试，而话术仍然只有一处。
export function byReasonText(byReason, labelOf) {
  return byReasonList(byReason)
    .map(({ reason, n }) => `${labelOf(reason)} ${n}`)
    .join(' · ');
}

// 处置表单。空说明 → 不可提交：一次没有说法的处置，与「没人管」在台账上无法区分
// （服务端同样 400，这里是同一条判据的第二份，为的是不让人打完字才被拒）。
export function ackFormState({ note } = {}) {
  const text = String(note ?? '');
  const trimmed = text.trim();
  const problems = {};
  if (trimmed === '') problems.note = 'empty';
  else if (text.length > ACK_NOTE_MAX) problems.note = 'tooLong';
  return {
    problems,
    chars: text.length,
    canSubmit: Object.keys(problems).length === 0,
    payload: { note: trimmed },
  };
}

// 端点错误码 → i18n 键。ALREADY_ACKED 单独一句：它不是失败，是**别人已经处置过了**
// （单向表的正常竞态），而一句"操作失败"会让人以为自己写的说明丢了。
export const ACK_ERR_KEYS = Object.freeze({
  ALREADY_ACKED: 'admin.escalations.err.alreadyAcked',
  RULE_VIOLATION: 'admin.escalations.err.emptyNote',
});

function str(v) { return typeof v === 'string' ? v : ''; }
function strArray(v) {
  return (Array.isArray(v) ? v : []).filter((s) => typeof s === 'string' && s.trim() !== '');
}
